import React, { FC, useCallback, useRef } from 'react'

import { css, cx, LinariaClassName } from '@linaria/core'
import { styled } from '@linaria/react'
import { useDispatch } from 'react-redux'

import {
  incognitoSavePositionAction,
  promoIncreaseVotedCounter,
} from 'actions/rating/promoNextAction'
import { switchIncognitoUserAction } from 'actions/user/incognitoUserAction'
import { VoidHandler } from 'common/types'
import { BannerPlaces } from 'components/banner/banner.constants'
import { GenericBanner } from 'components/banner/GenericBanner'
import { BannerLoadable } from 'components/banner/Rating/BannerLoadable'
import { KeyboardControl } from 'components/block/KeyboardControl/KeyboardControl'
import { FeaturedPhotos } from 'components/block/Notice/FeaturedPhotos/FeaturedPhotos'
import { Incognito } from 'components/block/Notice/Incognito/Incognito'
import { ClipPathOuter } from 'components/block/Profile/ProfileMain/profileMain.styled'
import { breakpoints } from 'components/designSystem/shared/breakpoints'
import { RatingPromo } from 'components/page/PhotoRating/PhotoRating.constants'
import { featuredPhotosPath } from 'components/paths'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { push } from 'functions/router'
import { useLayoutContext } from 'hooks/useLayoutContext'

import { Direction } from '../Rating.types'
import { DraggableRef, RatingDragDropMemo } from '../RatingDragDrop'

export const RatingNotice: FC<{
  promoName: RatingPromo
  enable: boolean
  /** Оставлю здесь если вдруг понадобится callback в parent component */
  onVoteEvent?: Function
}> = ({ promoName, enable }) => {
  const dispatch = useDispatch()
  const draggableRef = useRef<DraggableRef>(null)
  const { baseUrl } = useLayoutContext()

  const handleEvent = (direction: Direction) => {
    draggableRef.current?.move(direction)
  }

  const handlePromo = useCallback(
    (direction: Direction) => {
      switch (promoName) {
        case RatingPromo.incognito:
          dispatch(incognitoSavePositionAction())

          if (direction === Direction.right) {
            dispatch(switchIncognitoUserAction())
          }

          break

        case RatingPromo.featured:
          dispatch(promoIncreaseVotedCounter())

          if (direction === Direction.right) {
            dispatch(push(mergeAllUrls(baseUrl, featuredPhotosPath)))
          }

          break

        case RatingPromo.banner:
        default:
          dispatch(promoIncreaseVotedCounter())
          break
      }
    },
    [dispatch, promoName, baseUrl]
  )

  return (
    <RatingDragDropMemo
      onHandleEventEnd={(direction) => handlePromo(direction)}
      enable={enable}
      ref={draggableRef}
      hasChangeBodyBackground={false}
      disableTrail={true}
    >
      <Parent
        className={cx(
          enable && customPromoCss[promoName],
          colorMapCss[promoName]
        )}
      >
        <PromoNoticeSwitcher
          key={promoName}
          promoName={promoName}
          onHandleClick={handleEvent}
        />
      </Parent>

      {enable && (
        <KeyboardControl onHandleKeyboard={(action) => handleEvent(action)} />
      )}
    </RatingDragDropMemo>
  )
}

const PromoNoticeSwitcher: FC<{
  promoName: RatingPromo
  onHandleClick?: VoidHandler
}> = ({ promoName, onHandleClick }) => {
  switch (promoName) {
    case RatingPromo.featured:
      return <FeaturedPhotos onHandleClick={onHandleClick} />
    case RatingPromo.incognito:
      return <Incognito onHandleClick={onHandleClick} />
    case RatingPromo.banner:
      return (
        <GenericBanner
          place={BannerPlaces.ratingBanner}
          banner={<BannerLoadable onHandleClick={onHandleClick} />}
        />
      )
    default:
      return null
  }
}

const Parent = styled(ClipPathOuter)`
  height: 100%;
`

const customPromoCss: Record<RatingPromo, LinariaClassName | null> = {
  [RatingPromo.incognito]: css`
    @media screen and (min-width: ${breakpoints.tablet}px) {
      height: calc(100% + 2px);
      width: calc(100% + 2px);
      margin: -1px;
    }
  `,
  [RatingPromo.featured]: null,
  [RatingPromo.banner]: null,
}

const colorMapCss: Record<RatingPromo, LinariaClassName | null> = {
  [RatingPromo.incognito]: css`
    background-color: transparent;
  `,
  [RatingPromo.featured]: css`
    background-color: var(--background-page);
  `,
  [RatingPromo.banner]: null,
}
